import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';
import { workspacePath } from '../nav';
import { useSession } from '../session';
import { OpenPrompt, OrgTile, useOpenSession, type Openable } from '../openSession';

/**
 * Every org this person can open, across every workspace. The home page offers the recent few.
 *
 * Grouped by workspace, because the same customer can have a production org and two sandboxes, and two
 * customers can each have one called "Production". Seeing which company an org belongs to before opening
 * it is the difference between a routine session and one in somebody else's data.
 */
export function OpenOrg() {
  const { me } = useSession();
  const [orgs, setOrgs] = useState<Openable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { open, busy, prompt, dismissPrompt } = useOpenSession(setError);

  const load = useCallback(async () => {
    try {
      const r = await api<{ orgs: Openable[] }>('/api/me/openable');
      setOrgs(r.orgs);
    } catch (err) {
      setOrgs([]);
      setError(err instanceof ApiError ? err.message : 'Could not load your orgs');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // In the order the server sent them, which keeps each workspace's orgs in its own order too.
  const groups: { tenantId: string; orgs: Openable[] }[] = [];
  for (const o of orgs ?? []) {
    const group = groups.find((g) => g.tenantId === o.tenantId);
    if (group) group.orgs.push(o);
    else groups.push({ tenantId: o.tenantId, orgs: [o] });
  }
  const nameOf = (tenantId: string) => me?.memberships.find((m) => m.tenantId === tenantId)?.name ?? 'Workspace';

  return (
    <>
      <PageHeader title="Open a customer org" />
      <p className="lede">
        Opening one logs your browser into Salesforce as the chosen user, and records who, when and why.
      </p>
      {error && <div className="alert">{error}</div>}
      {prompt && (
        <OpenPrompt prompt={prompt} onSubmit={(extra) => void open(prompt.org, extra)} onCancel={dismissPrompt} />
      )}

      {orgs === null && <p className="muted">Loading…</p>}
      {orgs?.length === 0 && (
        <div className="notice">You do not hold access to any customer org yet. An admin grants it per org.</div>
      )}
      {groups.map((g) => (
        <section key={g.tenantId} className="group">
          <div className="group-head">
            <h2>{nameOf(g.tenantId)}</h2>
            <Link to={workspacePath(g.tenantId)} className="small">
              Go to workspace
            </Link>
          </div>
          <div className="tiles">
            {g.orgs.map((o) => (
              <OrgTile key={o.customerOrgId + o.identityId} org={o} busy={busy} onOpen={() => void open(o)} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
